import type { DebouncedFuncLeading } from 'lodash';
import Image from 'next/image';
import React from 'react';
import type { Id } from 'react-toastify';

import { CreditYellowIcon, FarmingAiriIcon, IconCheckAttendance, Image1Mission } from '@/assets';
import { Buttons } from '@/components';
import AvatarPlaceholder from '@/components/avatar-default';
import { CREDIT_POINTS } from '@/configs/aig';
import type { Attendances } from '@/contents/attendance';
import LoginModal from '@/layouts/header/Login';
import { MARKETPLACE_SUB_NAME } from '@/layouts/sidebar';
import { useAuth } from '@/provider/UserProvider';

interface AttendanceProps {
  handleClaimDaily: DebouncedFuncLeading<() => Promise<void | Id>>;
  historyAttendance: any;
  attendances: Attendances[] | undefined;
  disclosure: any;
}

const Attendance: React.FC<AttendanceProps> = ({
  handleClaimDaily,
  historyAttendance,
  attendances,
  disclosure,
}) => {
  const { isOpen, onOpenChange, onClose } = disclosure;
  const bgImage = '/images/bg-daily-mission.png';
  const { auth, isLoginClient } = useAuth();

  return (
    <div className="main-container max-w-[1048px] pt-10">
      <div>
        <div
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 100%',
          }}
          className="w-[101.8%] "
        >
          <div className="rounded-[32px] px-[40px] py-[52px] md:p-[24px]">
            <div>
              <p className="text-[40px] font-bold leading-[60px] text-white md:text-[24px] md:leading-9">
                My Credits
              </p>
              <p className="max-w-[460px] text-[16px] font-normal leading-6 text-white md:text-[14px] md:leading-5">
                Fruitful incentives to come, earn more Credits than today for subscribable
                conversion, event benefits, and beyond!
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="py-4 sm:py-0">
        <div className="flex justify-between md:block">
          <div className="flex md:pb-2">
            {isLoginClient ? (
              <div className="flex items-center">
                <AvatarPlaceholder
                  className="h-[55px] w-[55px] rounded-[14px] object-cover"
                  src={auth.user.picture}
                  address={auth.user.publicAddress}
                />
                <div className="pl-4 sm:flex sm:flex-col">
                  <p className="text-20 font-semibold sm:text-14">{auth.user.username || '---'}</p>{' '}
                  <span className="text-14 font-normal text-orange-500 sm:text-12">
                    My Credits: {auth?.user?.credit || 0}
                  </span>{' '}
                  <span className="sm:hidden"> | </span>
                  <span className="text-14 font-normal text-orange-500 sm:text-12">
                    Total Earn AIRI: {historyAttendance?.totalEarn || 0}
                  </span>{' '}
                  <span className="sm:hidden"> | </span>
                  <span className="text-14 font-normal text-orange-500 sm:text-12">
                    Total Transferred AIRI: {historyAttendance?.totalEarnTransferred || 0}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <Image
                  src={Image1Mission}
                  alt="avatarImag"
                  width={55}
                  height={55}
                  className="h-[55px] w-[55px] rounded-[14px] object-cover"
                />
                <div className="pl-4">
                  <p className="self-center pb-1 text-20 font-semibold leading-8">
                    Log in to aiRight
                  </p>
                  <span className="self-center text-14 font-normal leading-5 text-gray-500">
                    Log in daily to claim your reward.
                  </span>
                </div>
              </>
            )}
          </div>

          <Buttons
            aria-checked={attendances?.find((item) => item.isToday)?.checkEd}
            typeBtn="primary"
            isDisabled={attendances?.find((item) => item.isToday)?.checkEd}
            className="w-[150px] self-center px-[35px] py-[15px] sm:h-[32px] sm:w-[110px]"
            onClick={handleClaimDaily}
          >
            <p className="font-semibold leading-5 sm:text-14">Claim now</p>
          </Buttons>
        </div>
        <div className="grid grid-cols-7 items-center gap-7 pt-6 lg:grid-cols-4 sm:!gap-2">
          {attendances &&
            attendances?.map((itemDate: Attendances, i) => (
              <div
                onClick={() => {
                  Boolean(itemDate.isToday) && !itemDate.checkEd && handleClaimDaily();
                }}
                aria-checked={itemDate.checkEd}
                aria-expanded={Boolean(itemDate.isToday)}
                aria-disabled={Boolean(itemDate.isToday) && !itemDate.checkEd}
                className="flex h-[150px] w-[126px] flex-col items-center justify-between rounded-2xl border-2 border-gray-100 p-[10px] aria-checked:!border-green-500 aria-checked:bg-green-50 aria-disabled:cursor-pointer aria-expanded:border-yellow-600 lg:w-auto lg:p-0 sm:h-fit"
                key={i}
              >
                <p className="text-18 font-semibold leading-7 sm:text-12 sm:leading-[23px]">
                  Day {i + 1}
                </p>
                {itemDate.isEndDay ? (
                  <p className="sm:!my-1">
                    <FarmingAiriIcon className="sm:m-[-15px] sm:scale-50" />
                  </p>
                ) : itemDate.checkEd ? (
                  <p className="">
                    <IconCheckAttendance className="sm:m-[-15px] sm:scale-50" />
                  </p>
                ) : (
                  <p className="flex h-[48px] w-[48px] items-center justify-center rounded-[53px] bg-yellow-100 px-[14px] py-1 sm:h-fit sm:w-fit sm:px-3 sm:py-2">
                    <CreditYellowIcon className="sm:m-[-15px] sm:scale-[.4]" />
                  </p>
                )}
                {itemDate.isEndDay && (
                  <p className="text-center text-16 font-semibold text-blue-500 sm:text-12">
                    {CREDIT_POINTS.airi} AIRI
                  </p>
                )}
                <p
                  aria-checked={itemDate.checkEd}
                  className="text-center text-16 font-semibold text-yellow-700 aria-checked:text-green-500 sm:text-12"
                >
                  {itemDate.isEndDay && '+'} {CREDIT_POINTS.login} Credit
                </p>
              </div>
            ))}
        </div>
      </div>
      <div className="mt-5 lg:mb-5">
        <span className="text-orange-500">
          *To earn 100 AIRI during this event, you must hold an NFT at event{' '}
          <span
            onClick={() => {
              window.open(`https://${MARKETPLACE_SUB_NAME}.airight.io`, '_blank');
            }}
            className="cursor-pointer text-blue-500 hover:underline"
          >
            Samorai Coders: Leading the AI Revolution in Web3
          </span>
        </span>
        <h3 className="mb-2 text-16 font-semibold underline">Daily Attendance Reward Rule:</h3>
        <div className="ml-3">
          <p>
            <strong>1. Daily Reward: </strong>Users earn 10 credits for each consecutive day they
            check in. The check-in time is based on UTC +0.
          </p>
          <p>
            <strong>2. 7-Day Streak Bonus: </strong>On the 7th consecutive day, in addition to the
            daily 10 credits, users will receive an additional bonus of 100 AIRI. Condition: Users
            will only receive the AIRI bonus if they possess an NFT in the{' '}
            <span
              onClick={() => {
                window.open(`https://${MARKETPLACE_SUB_NAME}.airight.io`, '_blank');
              }}
              className="cursor-pointer text-blue-500 hover:underline"
            >
              event
            </span>
            .
          </p>
          <p>
            <strong>3. Streak Reset: </strong>After completing the 7-day streak, the counter resets,
            starting a new cycle.
          </p>
          <p>
            <strong>4. Missed Day: </strong>If a user misses a day, the streak resets, and they must
            start a new 7-day cycle to earn rewards.
          </p>
        </div>
      </div>
      <LoginModal isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose} />
    </div>
  );
};

export default Attendance;
